import { Alert, Button, Card, Form, Input, Select, Space, Table, Tag, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type Row = {
  id: string;
  template_code: string;
  name: string;
  channel: string;
  audience: string;
  event_code: string;
  status: string;
  is_platform?: boolean;
};

export default function CommsTemplateListPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.comms.manage') || user?.permissions?.includes('education.admin')
  );
  const [showForm, setShowForm] = useState(false);

  const list = useQuery({
    queryKey: ['education-comm-templates'],
    queryFn: async () => {
      const res = await api.get<Row[]>('/education/comms/templates');
      return Array.isArray(res.data) ? res.data : [];
    },
  });

  const create = useMutation({
    mutationFn: (values: Record<string, unknown>) => api.post('/education/comms/templates', values),
    onSuccess: () => {
      message.success(t('commTemplateCreated'));
      setShowForm(false);
      queryClient.invalidateQueries({ queryKey: ['education-comm-templates'] });
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  return (
    <EducationPageChrome
      title={t('commTemplates')}
      description={t('commTemplatesIntro')}
      extra={
        <Space>
          <Button onClick={() => navigate('/education/comms')}>{t('comms')}</Button>
          {canManage ? (
            <Button type="primary" onClick={() => setShowForm((v) => !v)}>
              {t('newCommTemplate')}
            </Button>
          ) : null}
        </Space>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert type="info" showIcon message={t('commsOutboundHint')} />
        {canManage && showForm ? (
          <Card size="small" title={t('newCommTemplate')}>
            <Form
              layout="vertical"
              style={{ maxWidth: 640 }}
              initialValues={{
                channel: 'both',
                audience: 'guardians',
                event_code: 'education.comm.message',
              }}
              onFinish={(values) => create.mutate(values)}
            >
              <Form.Item name="template_code" label={t('templateCode')} rules={[{ required: true }]}>
                <Input maxLength={40} />
              </Form.Item>
              <Form.Item name="name" label={t('name')} rules={[{ required: true }]}>
                <Input maxLength={200} />
              </Form.Item>
              <Form.Item name="channel" label={t('channel')} rules={[{ required: true }]}>
                <Select
                  options={[
                    { value: 'in_app', label: t('channelInApp') },
                    { value: 'email', label: t('channelEmail') },
                    { value: 'both', label: t('channelBoth') },
                  ]}
                />
              </Form.Item>
              <Form.Item name="subject_template" label={t('subjectTemplate')} rules={[{ required: true }]}>
                <Input maxLength={300} />
              </Form.Item>
              <Form.Item name="body_template" label={t('bodyTemplate')} rules={[{ required: true }]}>
                <Input.TextArea rows={5} maxLength={4000} />
              </Form.Item>
              <Form.Item name="event_code" label={t('eventCode')}>
                <Input maxLength={80} />
              </Form.Item>
              <Button type="primary" htmlType="submit" loading={create.isPending}>
                {t('save')}
              </Button>
            </Form>
          </Card>
        ) : null}
        <Card size="small">
          <Table
            rowKey="id"
            size="small"
            loading={list.isLoading}
            dataSource={list.data ?? []}
            pagination={{ pageSize: 40 }}
            columns={[
              { title: t('templateCode'), dataIndex: 'template_code', width: 160 },
              { title: t('name'), dataIndex: 'name' },
              { title: t('channel'), dataIndex: 'channel', width: 100 },
              { title: t('eventCode'), dataIndex: 'event_code' },
              {
                title: t('status'),
                dataIndex: 'status',
                width: 120,
                render: (v: string, row: Row) => <Tag>{row.is_platform ? 'platform' : v}</Tag>,
              },
            ]}
          />
        </Card>
      </Space>
    </EducationPageChrome>
  );
}
